import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../context/BranchContext';
import { orderApprovalAPI, orderAPI, orderStaffAuditAPI } from '../../services';
import { toast } from 'react-toastify';
import PaginationComponent from '../../components/PaginationComponent';
import DateRangePicker, { calcDatePreset } from '../../components/fields/DateRangePicker';
import moment from 'moment-timezone';

const TIMEZONE = 'Asia/Ho_Chi_Minh';
const DEFAULT_PRESET = 'last7days';

const REQUEST_TYPE_LABELS = {
  DELETE_ORDER: 'Xóa đơn',
  DELETE_ORDER_ITEM: 'Xóa món',
};
const REQUEST_TYPE_VARIANTS = {
  DELETE_ORDER: 'danger',
  DELETE_ORDER_ITEM: 'warning',
};

const STATUS_LABELS = {
  PENDING: 'Chờ duyệt',
  APPROVED: 'Đã duyệt',
  REJECTED: 'Từ chối',
  EXPIRED: 'Hết hạn',
};
const STATUS_VARIANTS = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  EXPIRED: 'secondary',
};

const ORDER_STATUS_LABELS = {
  PENDING: 'Chờ xác nhận',
  CONFIRMED: 'Đã xác nhận',
  PREPARING: 'Đang chuẩn bị',
  READY: 'Sẵn sàng phục vụ',
  SERVED: 'Đã phục vụ',
  COMPLETED: 'Hoàn thành',
  CANCELLED: 'Đã hủy',
};
const ORDER_STATUS_VARIANTS = {
  PENDING: 'warning',
  CONFIRMED: 'info',
  PREPARING: 'primary',
  READY: 'secondary',
  SERVED: 'secondary',
  COMPLETED: 'success',
  CANCELLED: 'danger',
};
const PAYMENT_STATUS_LABELS = {
  PENDING: 'Chưa thanh toán',
  PAID: 'Đã thanh toán',
  REFUNDED: 'Đã hoàn tiền',
};
const PAYMENT_STATUS_VARIANTS = { PENDING: 'warning', PAID: 'success', REFUNDED: 'info' };
const PAYMENT_METHOD_LABELS = {
  CASH: 'Tiền mặt',
  BANK_TRANSFER: 'Chuyển khoản',
  CARD: 'Thẻ',
  MOMO: 'MoMo',
  VNPAY: 'VNPay',
};
const ORDER_TYPE_LABELS = { DINE_IN: 'Tại quán', TAKEAWAY: 'Mang đi', DELIVERY: 'Giao hàng' };
const ORDER_STAFF_ACTION_LABELS = {
  LINE_ITEMS_ADDED: 'Thêm món',
  DELETE_LINE_ITEMS_REQUESTED: 'Yêu cầu xóa món',
  LINE_ITEMS_REMOVED: 'Đã xóa món (duyệt)',
};

const fmt = (dt) => (dt ? moment.tz(dt, TIMEZONE).format('DD/MM/YYYY HH:mm') : '—');
const fmtCurrency = (v) =>
  v == null ? '—' : new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(Number(v));
const label = (dict, val, fallback = '—') => dict[val] || fallback;

export default function OrderDeletionAuditPage() {
  // #175019 — chi nhánh chỉ chọn ở thanh trên đầu; trang không còn ô lọc riêng.
  const { selectedBranch } = useBranch();

  const initDates = calcDatePreset(DEFAULT_PRESET);
  const [fromDate, setFromDate] = useState(initDates.from);
  const [toDate, setToDate] = useState(initDates.to);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  const [data, setData] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);

  // Approval request detail (left pane)
  const [approvalDetail, setApprovalDetail] = useState(null);
  // Full order detail (right pane / bottom)
  const [detailOrder, setDetailOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [orderAuditEvents, setOrderAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPaymentProof, setShowPaymentProof] = useState(false);

  const branchFilter = selectedBranch?.id || '';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        startDate: fromDate ? `${fromDate}T00:00:00+07:00` : undefined,
        endDate: toDate ? `${toDate}T23:59:59+07:00` : undefined,
      };
      if (branchFilter) params.branchId = branchFilter;
      if (typeFilter) params.requestType = typeFilter;
      if (statusFilter) params.status = statusFilter;

      const res = await orderApprovalAPI.getDeletionRequests(params);
      setData(res.data ?? []);
      setMeta(res.meta ?? null);
    } catch {
      toast.error('Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, [page, fromDate, toDate, branchFilter, typeFilter, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDateChange = (from, to) => { setFromDate(from); setToDate(to); setPage(1); };
  const handleFilter = (setter) => (e) => { setter(e.target.value); setPage(1); };

  const openDetail = (row) => {
    setApprovalDetail(row);
    setDetailOrder(null);
    setOrderAuditEvents([]);
    setShowPaymentProof(false);
    setShowDetailModal(true);

    const orderId = row.request_data?.order_id;
    if (!orderId) return;

    setDetailLoading(true);
    setAuditLoading(true);
    Promise.all([
      orderAPI.getOrderById(orderId),
      orderStaffAuditAPI
        .getEvents({ order_id: orderId, page: 1, limit: 80 })
        .catch(() => ({ data: { data: [] } })),
    ])
      .then(([orderRes, auditRes]) => {
        setDetailOrder(orderRes?.data || orderRes);
        const ev = auditRes?.data?.data ?? auditRes?.data ?? [];
        setOrderAuditEvents(Array.isArray(ev) ? ev : []);
      })
      .catch(() => toast.error('Không tải được chi tiết đơn hàng'))
      .finally(() => { setDetailLoading(false); setAuditLoading(false); });
  };

  const counts = data.reduce(
    (acc, r) => {
      acc.total++;
      if (r.status === 'PENDING') acc.pending++;
      else if (r.status === 'APPROVED') acc.approved++;
      else if (r.status === 'REJECTED') acc.rejected++;
      return acc;
    },
    { total: 0, pending: 0, approved: 0, rejected: 0 },
  );

  return (
    <PageLayout title="Audit Xóa Đơn / Xóa Món">
      {/* Summary cards */}
      <Row className="mb-3 g-2">
        {[
          { label: 'Tổng yêu cầu', value: meta?.total ?? '—', color: 'primary' },
          { label: 'Chờ duyệt', value: counts.pending, color: 'warning' },
          { label: 'Đã duyệt', value: counts.approved, color: 'success' },
          { label: 'Từ chối', value: counts.rejected, color: 'danger' },
        ].map((c) => (
          <Col key={c.label} xs={6} md={3}>
            <Card className={`border-${c.color} text-center`}>
              <Card.Body className="py-2">
                <div className="fs-4 fw-bold">{c.value}</div>
                <div className="text-muted small">{c.label}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filters */}
      <Card className="mb-3">
        <Card.Body>
          <DateRangePicker
            fromDate={fromDate}
            toDate={toDate}
            onChange={handleDateChange}
            defaultPreset={DEFAULT_PRESET}
          />
          <Row className="g-2 mt-2 align-items-end">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="small">Loại yêu cầu</Form.Label>
                <Form.Select size="sm" value={typeFilter} onChange={handleFilter(setTypeFilter)}>
                  <option value="">Tất cả</option>
                  <option value="DELETE_ORDER">Xóa đơn</option>
                  <option value="DELETE_ORDER_ITEM">Xóa món</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="small">Trạng thái</Form.Label>
                <Form.Select size="sm" value={statusFilter} onChange={handleFilter(setStatusFilter)}>
                  <option value="">Tất cả</option>
                  <option value="PENDING">Chờ duyệt</option>
                  <option value="APPROVED">Đã duyệt</option>
                  <option value="REJECTED">Từ chối</option>
                  <option value="EXPIRED">Hết hạn</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>
          ) : (
            <Table responsive hover className="mb-0 small">
              <thead className="table-light">
                <tr>
                  <th>Thời gian</th>
                  <th>Chi nhánh</th>
                  <th>Loại</th>
                  <th>Số đơn</th>
                  <th>SĐT khách</th>
                  <th>Tên khách</th>
                  <th>Số khách</th>
                  <th>NV phục vụ</th>
                  <th>NV thanh toán</th>
                  <th>NV yêu cầu xóa</th>
                  <th>NV duyệt</th>
                  <th>Lý do</th>
                  <th>Trạng thái</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 ? (
                  <tr>
                    <td colSpan={14} className="text-center text-muted py-4">Không có dữ liệu</td>
                  </tr>
                ) : data.map((r) => (
                  <tr key={r.id}>
                    <td className="text-nowrap">{fmt(r.created_at)}</td>
                    <td>{r.branch_name ?? '—'}</td>
                    <td>
                      <Badge bg={REQUEST_TYPE_VARIANTS[r.request_type] ?? 'secondary'}>
                        {REQUEST_TYPE_LABELS[r.request_type] ?? r.request_type}
                      </Badge>
                    </td>
                    <td className="text-nowrap">{r.request_data?.order_number ?? '—'}</td>
                    <td>{r.customer_phone ?? '—'}</td>
                    <td>{r.customer_name ?? '—'}</td>
                    <td className="text-center">{r.member_count ?? '—'}</td>
                    <td>{r.server_name ?? '—'}</td>
                    <td>{r.completer_name ?? '—'}</td>
                    <td>{r.requester_name ?? '—'}</td>
                    <td>{r.approver_name ?? '—'}</td>
                    <td
                      style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      title={r.reason ?? ''}
                    >
                      {r.reason ?? '—'}
                    </td>
                    <td>
                      <Badge bg={STATUS_VARIANTS[r.status] ?? 'secondary'}>
                        {STATUS_LABELS[r.status] ?? r.status}
                      </Badge>
                    </td>
                    <td>
                      <Button size="sm" variant="outline-primary" onClick={() => openDetail(r)}>
                        Xem chi tiết
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
        {meta && meta.total_pages > 1 && (
          <Card.Footer>
            <PaginationComponent
              currentPage={page}
              totalPages={meta.total_pages}
              onPageChange={setPage}
            />
          </Card.Footer>
        )}
      </Card>

      {/* Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Chi tiết đơn hàng
            {approvalDetail && (
              <Badge
                bg={REQUEST_TYPE_VARIANTS[approvalDetail.request_type] ?? 'secondary'}
                className="ms-2 fs-6"
              >
                {REQUEST_TYPE_LABELS[approvalDetail.request_type] ?? approvalDetail.request_type}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Approval request info box */}
          {approvalDetail && (
            <Card className="mb-3 border-secondary">
              <Card.Body className="py-2 small">
                <Row className="g-2">
                  <Col xs={6} md={3}>
                    <span className="text-muted">Trạng thái xét duyệt</span>
                    <div>
                      <Badge bg={STATUS_VARIANTS[approvalDetail.status] ?? 'secondary'}>
                        {STATUS_LABELS[approvalDetail.status] ?? approvalDetail.status}
                      </Badge>
                    </div>
                  </Col>
                  <Col xs={6} md={3}>
                    <span className="text-muted">NV yêu cầu xóa</span>
                    <div className="fw-semibold">{approvalDetail.requester_name ?? '—'}</div>
                  </Col>
                  <Col xs={6} md={3}>
                    <span className="text-muted">NV duyệt</span>
                    <div className="fw-semibold">{approvalDetail.approver_name ?? '—'}</div>
                  </Col>
                  <Col xs={6} md={3}>
                    <span className="text-muted">Thời gian yêu cầu</span>
                    <div>{fmt(approvalDetail.created_at)}</div>
                  </Col>
                  <Col xs={12} md={6}>
                    <span className="text-muted">Lý do xóa</span>
                    <div>{approvalDetail.reason ?? '—'}</div>
                  </Col>
                  {approvalDetail.rejected_reason && (
                    <Col xs={12} md={6}>
                      <span className="text-muted">Lý do từ chối</span>
                      <div className="text-danger">{approvalDetail.rejected_reason}</div>
                    </Col>
                  )}
                  {approvalDetail.request_type === 'DELETE_ORDER_ITEM' && (
                    <>
                      <Col xs={6} md={3}>
                        <span className="text-muted">Món bị xóa</span>
                        <div className="fw-semibold">{approvalDetail.request_data?.menu_item_name ?? '—'}</div>
                      </Col>
                      <Col xs={6} md={3}>
                        <span className="text-muted">Giá trị món</span>
                        <div>{fmtCurrency(approvalDetail.request_data?.item_subtotal)}</div>
                      </Col>
                    </>
                  )}
                </Row>
              </Card.Body>
            </Card>
          )}

          <hr className="my-2" />

          {/* Full order detail */}
          {detailLoading && (
            <div className="text-center py-4"><Spinner animation="border" /></div>
          )}

          {!detailLoading && !detailOrder && (
            <div className="text-muted text-center py-3">Không có dữ liệu đơn hàng.</div>
          )}

          {detailOrder && (
            <>
              <div className="mb-3">
                <h5 className="mb-1">{detailOrder.order_number || detailOrder.id}</h5>
                <div className="d-flex gap-2 flex-wrap">
                  <Badge bg={ORDER_STATUS_VARIANTS[detailOrder.status] || 'secondary'}>
                    {label(ORDER_STATUS_LABELS, detailOrder.status, detailOrder.status)}
                  </Badge>
                  <Badge bg={PAYMENT_STATUS_VARIANTS[detailOrder.payment_status] || 'secondary'}>
                    {label(PAYMENT_STATUS_LABELS, detailOrder.payment_status, detailOrder.payment_status)}
                  </Badge>
                  <Badge bg="light" text="dark">
                    {label(ORDER_TYPE_LABELS, detailOrder.order_type, detailOrder.order_type)}
                  </Badge>
                </div>
              </div>

              <div className="mb-2">
                <strong>Chi nhánh:</strong>{' '}
                {detailOrder.branch?.name || detailOrder.branch_name || detailOrder.branch_id}
              </div>
              <div className="mb-2">
                <strong>Bàn:</strong>{' '}
                {detailOrder.table?.number || detailOrder.table?.name || detailOrder.table_id || 'N/A'}
              </div>
              <div className="mb-2">
                <strong>Tài khoản nhận:</strong>{' '}
                {detailOrder.bank_account
                  ? `${detailOrder.bank_account.bank_name} - ${detailOrder.bank_account.account_no} (${detailOrder.bank_account.account_name})`
                  : 'N/A'}
              </div>
              <div className="mb-2">
                <strong>Khách hàng:</strong>{' '}
                {detailOrder.customer_name || 'Khách lẻ'}
                {detailOrder.customer_phone && <> ({detailOrder.customer_phone})</>}
              </div>
              <div className="mb-3">
                <strong>Số khách:</strong>{' '}
                {detailOrder.member_count != null ? `${detailOrder.member_count} người` : 'N/A'}
              </div>

              {/* Audit events */}
              <h6 className="mt-2">Nhật ký thao tác (nhân viên — món)</h6>
              <div className="border rounded mb-3 bg-light">
                {auditLoading && (
                  <div className="p-2 small text-muted">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Đang tải nhật ký…
                  </div>
                )}
                {!auditLoading && orderAuditEvents.length === 0 && (
                  <div className="p-2 small text-muted">Chưa có sự kiện.</div>
                )}
                {!auditLoading && orderAuditEvents.length > 0 && (
                  <Table striped size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Thời gian</th>
                        <th>Hành động</th>
                        <th>Nhân viên</th>
                        <th>Ca</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderAuditEvents.map((ev) => (
                        <tr key={ev.id}>
                          <td className="text-nowrap small">{fmt(ev.created_at)}</td>
                          <td className="small">{ORDER_STAFF_ACTION_LABELS[ev.action_type] || ev.action_type}</td>
                          <td className="small">{ev.actor?.full_name || ev.actor?.username || ev.actor_id || '—'}</td>
                          <td className="small text-muted">
                            {ev.shift?.id
                              ? `${fmt(ev.shift.start_time)} → ${fmt(ev.shift.end_time)}`
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>

              {/* Order items */}
              <h6>Món gọi</h6>
              <div className="border rounded mb-3">
                <Table striped bordered size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Món</th>
                      <th>SL</th>
                      <th>Tùy chọn</th>
                      <th>Thành tiền</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detailOrder.items || []).map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="fw-semibold">
                            {item.menu_item?.name || item.menu_item_name || item.menu_item_id}
                          </div>
                          {item.customer_phone && (
                            <small className="text-muted d-block">SĐT món: {item.customer_phone}</small>
                          )}
                          {item.notes && (
                            <small className="text-muted d-block">Ghi chú: {item.notes}</small>
                          )}
                        </td>
                        <td>{item.quantity}</td>
                        <td>
                          {item.selected_modifiers_display ? (
                            <ul className="mb-0 small ps-3">
                              {Object.entries(item.selected_modifiers_display).map(([g, s]) => (
                                <li key={g}>
                                  <strong>{g}:</strong> {Array.isArray(s) ? s.join(', ') : s}
                                </li>
                              ))}
                            </ul>
                          ) : item.selected_modifiers ? (
                            <pre className="mb-0 small bg-light p-1 rounded">
                              {JSON.stringify(item.selected_modifiers, null, 2)}
                            </pre>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                        <td>{fmtCurrency(item.subtotal ?? item.quantity * item.unit_price)}</td>
                      </tr>
                    ))}
                    {(!detailOrder.items || detailOrder.items.length === 0) && (
                      <tr>
                        <td colSpan={4} className="text-center text-muted">Không có món nào.</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Totals + payment */}
              <div className="d-flex justify-content-between flex-wrap gap-2">
                <div className="small">
                  <div>Subtotal: {fmtCurrency(detailOrder.subtotal)}</div>
                  <div>Giảm giá: {fmtCurrency(detailOrder.discount_amount)}</div>
                  <div>Thuế: {fmtCurrency(detailOrder.tax_amount)}</div>
                  <div>Phụ thu: {fmtCurrency(detailOrder.surcharge_amount)}</div>
                </div>
                <div className="text-end">
                  <div className="fs-5 fw-bold">
                    Tổng thanh toán: {fmtCurrency(detailOrder.total_amount)}
                  </div>
                  <small className="text-muted">
                    Phương thức: {label(PAYMENT_METHOD_LABELS, detailOrder.payment_method, detailOrder.payment_method || 'N/A')}
                  </small>
                  {detailOrder.payment_proof_url && (
                    <div className="mt-2">
                      <div className="small text-muted mb-1">Ảnh chứng từ:</div>
                      {!showPaymentProof ? (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => setShowPaymentProof(true)}
                        >
                          Xem ảnh
                        </Button>
                      ) : (
                        <a href={detailOrder.payment_proof_url} target="_blank" rel="noreferrer">
                          <img
                            src={detailOrder.payment_proof_url}
                            alt="Payment proof"
                            className="rounded border"
                            width={200}
                            height={200}
                            style={{ objectFit: 'cover' }}
                          />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>Đóng</Button>
        </Modal.Footer>
      </Modal>
    </PageLayout>
  );
}
